import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Users, Trophy, Flag, AlertCircle, User, Clock, CheckCircle, ChevronDown, ChevronUp, Award, Zap, CloudRain, Car, Timer, Search } from 'lucide-react';
import { getRaces, getAllPredictionsForRace, getAllChampionshipPredictions, getDrivers, getTeams, getMyPredictions, getMyChampionshipPrediction, getTestSeason, getTestRaces, getSeasonSettings } from '../services/api';
import { formatDriverName } from '../utils/formatName';
import { isDeadlinePassed as checkDeadlinePassed, formatLocalTime } from '../utils/timeUtils';

const Voorspellingen = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  
  const [races, setRaces] = useState([]);
  const [selectedRace, setSelectedRace] = useState('');
  const [racePredictions, setRacePredictions] = useState([]);
  const [championshipPredictions, setChampionshipPredictions] = useState([]);
  const [myPredictions, setMyPredictions] = useState([]);
  const [myChampionshipPrediction, setMyChampionshipPrediction] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPredictions, setExpandedPredictions] = useState({});
  const [seasonSettings, setSeasonSettings] = useState({});
  
  // Compact view state - all races collapsed by default
  const [expandedRaces, setExpandedRaces] = useState({});
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  const toggleExpand = (username) => {
    setExpandedPredictions(prev => ({
      ...prev,
      [username]: !prev[username]
    }));
  };
  
  const toggleRaceExpand = (raceId) => {
    setExpandedRaces(prev => ({
      ...prev,
      [raceId]: !prev[raceId]
    }));
  };
  
  // Expand/collapse all races
  const expandAllRaces = () => {
    const allExpanded = {};
    myPredictions.forEach(pred => {
      allExpanded[`my_${pred.race_id}`] = true;
    });
    setExpandedRaces(allExpanded);
  };
  
  const collapseAllRaces = () => {
    setExpandedRaces({});
  };

  // Filter predictions by search
  const filteredPredictions = racePredictions.filter(pred => 
    pred.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Reset search when race changes
  useEffect(() => {
    setSearchQuery('');
  }, [selectedRace]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [racesData, driversData, teamsData, testSeasonData, testRacesData, settingsData] = await Promise.all([
          getRaces(2026),
          getDrivers(2026),
          getTeams(2026),
          getTestSeason().catch(() => null),
          getTestRaces().catch(() => []),
          getSeasonSettings(2026).catch(() => ({})),
        ]);
        
        // Combine regular races with test races if test season is active
        let allRaces = [...racesData];
        if (testSeasonData && testRacesData && testRacesData.length > 0) {
          // Mark test races with a special flag for UI purposes
          const markedTestRaces = testRacesData.map(race => ({
            ...race,
            isTestRace: true,
            name: `🧪 ${race.name}` // Add visual indicator
          }));
          // Put test races at the beginning since they happen before the regular season
          allRaces = [...markedTestRaces, ...racesData];
        }
        
        setRaces(allRaces);
        setDrivers(driversData);
        setTeams(teamsData);
        setSeasonSettings(settingsData || {});
        
        // Load user's own predictions if logged in
        if (user) {
          try {
            const [myPreds, myChampPred] = await Promise.all([
              getMyPredictions(),
              getMyChampionshipPrediction(2026),
            ]);
            setMyPredictions(myPreds);
            setMyChampionshipPrediction(myChampPred);
          } catch (err) {
            console.error('Error loading my predictions:', err);
          }
        }
        
        // Select first race with passed deadline
        const passedRace = racesData.find(r => r.status === 'completed');
        if (passedRace) {
          setSelectedRace(passedRace.race_id);
          await loadRacePredictions(passedRace.race_id);
        }
        
        // Try to load championship predictions
        await loadChampionshipPredictions();
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const loadRacePredictions = async (raceId) => {
    try {
      setError(null);
      const predictions = await getAllPredictionsForRace(raceId);
      setRacePredictions(predictions);
    } catch (error) {
      if (error.response?.status === 403) {
        setError('Voorspellingen zijn pas zichtbaar na de deadline');
      } else {
        setError('Kon voorspellingen niet laden');
      }
      setRacePredictions([]);
    }
  };

  const loadChampionshipPredictions = async () => {
    try {
      const predictions = await getAllChampionshipPredictions(2026);
      setChampionshipPredictions(predictions);
    } catch (error) {
      if (error.response?.status === 403) {
        // Deadline not passed yet
        setChampionshipPredictions([]);
      }
    }
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.driver_id === driverId);
    if (!driver) return driverId;
    return formatDriverName(driver.name);
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.team_id === teamId);
    return team ? team.name : teamId;
  };

  // Use shared deadline check from timeUtils (UTC-based)
  const isDeadlinePassed = (race) => checkDeadlinePassed(race);

  const handleRaceChange = async (raceId) => {
    setSelectedRace(raceId);
    const race = races.find(r => r.race_id === raceId);
    if (race && isDeadlinePassed(race)) {
      await loadRacePredictions(raceId);
    } else {
      // Don't call API for races before deadline — show message locally
      setError(null);
      setRacePredictions([]);
    }
  };

  // Get my prediction for a specific race
  const getMyRacePrediction = (raceId) => {
    return myPredictions.find(p => p.race_id === raceId && p.type === 'race');
  };

  const getMyQualifyingPrediction = (raceId) => {
    return myPredictions.find(p => p.race_id === raceId && p.type === 'qualifying');
  };

  // Group predictions by race
  const getMyPredictionsByRace = () => {
    const byRace = {};
    myPredictions.forEach(pred => {
      if (!byRace[pred.race_id]) {
        byRace[pred.race_id] = { race: null, qualifying: null };
      }
      if (pred.type === 'race') {
        byRace[pred.race_id].race = pred;
      } else if (pred.type === 'qualifying') {
        byRace[pred.race_id].qualifying = pred;
      }
    });
    return byRace;
  };

  const getRaceName = (raceId) => {
    const race = races.find(r => r.race_id === raceId);
    return race ? race.name : raceId;
  };

  const getRaceDate = (raceId) => {
    const race = races.find(r => r.race_id === raceId);
    return race ? new Date(race.date).toLocaleDateString('nl-NL') : '';
  };

  // Helper function to format score breakdown
  const renderScoreBreakdown = (scoreBreakdown) => {
    if (!scoreBreakdown) return null;
    
    const raceBreakdown = scoreBreakdown.race || {};
    const qualBreakdown = scoreBreakdown.qualifying || {};
    
    const breakdownItems = [];
    
    // Race position scores (P1-P5)
    for (let i = 1; i <= 5; i++) {
      const posKey = Object.keys(raceBreakdown).find(k => k.startsWith(`P${i}_`));
      if (posKey && raceBreakdown[posKey]) {
        const value = raceBreakdown[posKey];
        const driverId = posKey.split('_').slice(1).join('_');
        const driverName = getDriverName(driverId);
        breakdownItems.push({
          label: `Race P${i}: ${driverName}`,
          points: value.points || 0,
          type: 'position',
          detail: value.actual !== null 
            ? (value.diff === 0 ? `Exact juist! (P${value.actual})` : `Voorspeld P${value.predicted}, werkelijk P${value.actual}`)
            : (value.reason || 'Niet in uitslag'),
          sortOrder: i
        });
      }
    }
    
    // Bonus points - Race
    if (raceBreakdown.fastest_lap) {
      breakdownItems.push({ 
        label: 'Snelste Ronde Correct', 
        points: raceBreakdown.fastest_lap, 
        type: 'bonus',
        detail: '+10 punten',
        sortOrder: 10
      });
    }
    if (raceBreakdown.safety_car) {
      breakdownItems.push({ 
        label: 'Safety Car Correct', 
        points: raceBreakdown.safety_car, 
        type: 'bonus',
        detail: '+10 punten',
        sortOrder: 11
      });
    }
    if (raceBreakdown.virtual_safety_car) {
      breakdownItems.push({ 
        label: 'Virtual SC Correct', 
        points: raceBreakdown.virtual_safety_car, 
        type: 'bonus',
        detail: '+10 punten',
        sortOrder: 12
      });
    }
    if (raceBreakdown.dnf_count) {
      breakdownItems.push({ 
        label: 'DNF Aantal Correct', 
        points: raceBreakdown.dnf_count, 
        type: 'bonus',
        detail: '+10 punten',
        sortOrder: 13
      });
    }
    if (raceBreakdown.rain_race) {
      breakdownItems.push({ 
        label: 'Regen Race Correct', 
        points: raceBreakdown.rain_race, 
        type: 'bonus',
        detail: '+10 punten',
        sortOrder: 14
      });
    }
    
    // Qualifying scores (Q1, Q2, Q3)
    if (qualBreakdown.Q1_exact) {
      breakdownItems.push({
        label: 'Kwali P1 Correct',
        points: qualBreakdown.Q1_exact,
        type: 'qualifying',
        detail: 'Exact juist! +10 punten',
        sortOrder: 20
      });
    }
    if (qualBreakdown.Q2_exact) {
      breakdownItems.push({
        label: 'Kwali P2 Correct',
        points: qualBreakdown.Q2_exact,
        type: 'qualifying',
        detail: 'Exact juist! +10 punten',
        sortOrder: 21
      });
    }
    if (qualBreakdown.Q3_exact) {
      breakdownItems.push({
        label: 'Kwali P3 Correct',
        points: qualBreakdown.Q3_exact,
        type: 'qualifying',
        detail: 'Exact juist! +10 punten',
        sortOrder: 22
      });
    }
    
    // Pole time
    if (qualBreakdown.pole_time && qualBreakdown.pole_time.points) {
      const poleData = qualBreakdown.pole_time;
      breakdownItems.push({ 
        label: 'Pole Tijd', 
        points: poleData.points, 
        type: 'pole',
        detail: `Voorspeld: ${poleData.predicted}, Werkelijk: ${poleData.actual}`,
        sortOrder: 25
      });
    }
    
    // Sort by sortOrder
    breakdownItems.sort((a, b) => (a.sortOrder || 99) - (b.sortOrder || 99));
    
    return breakdownItems;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <div className="text-muted-foreground">Laden...</div>
      </div>
    );
  }

  // Calculate statistics for a race
  const calculateRaceStatistics = () => {
    if (!racePredictions || racePredictions.length === 0) return null;
    
    const stats = {
      total: racePredictions.length,
      rain: { yes: 0, no: 0 },
      safetyCar: { yes: 0, no: 0 },
      vsc: { yes: 0, no: 0 },
      dnf: {},
      pole: {},
      fastestLap: {},
      raceWinner: {},
      poleTimes: [],
      top5Predictions: []
    };
    
    racePredictions.forEach(pred => {
      const racePred = pred.race_prediction || {};
      const qualPred = pred.qualifying_prediction || {};
      
      // Rain
      if (racePred.rain_race === true || racePred.rain_race === 'ja') stats.rain.yes++;
      else if (racePred.rain_race === false || racePred.rain_race === 'nee') stats.rain.no++;
      
      // Safety Car
      if (racePred.safety_car === true || racePred.safety_car === 'ja') stats.safetyCar.yes++;
      else if (racePred.safety_car === false || racePred.safety_car === 'nee') stats.safetyCar.no++;
      
      // VSC
      if (racePred.virtual_safety_car === true || racePred.virtual_safety_car === 'ja') stats.vsc.yes++;
      else if (racePred.virtual_safety_car === false || racePred.virtual_safety_car === 'nee') stats.vsc.no++;
      
      // DNF count
      const dnf = racePred.dnf_count;
      if (dnf !== undefined && dnf !== null && dnf !== '') {
        stats.dnf[dnf] = (stats.dnf[dnf] || 0) + 1;
      }
      
      // Pole position (Q1)
      const pole = qualPred.qualifying?.[0];
      if (pole) {
        stats.pole[pole] = (stats.pole[pole] || 0) + 1;
      }
      
      // Fastest lap
      const fl = racePred.fastest_lap;
      if (fl) {
        stats.fastestLap[fl] = (stats.fastestLap[fl] || 0) + 1;
      }
      
      // Race winner (P1)
      const winner = racePred.top5?.[0];
      if (winner) {
        stats.raceWinner[winner] = (stats.raceWinner[winner] || 0) + 1;
      }
      
      // Pole times
      const poleTime = qualPred.pole_time;
      if (poleTime) {
        stats.poleTimes.push({ username: pred.username, time: poleTime });
      }
      
      // Top 5 predictions
      if (racePred.top5 && racePred.top5.length > 0) {
        stats.top5Predictions.push({
          username: pred.username,
          top5: racePred.top5.map(d => getDriverShortName(d))
        });
      }
    });
    
    return stats;
  };
  
  // Get driver short name (last name or abbreviation)
  const getDriverShortName = (driverId) => {
    const driver = drivers.find(d => d.driver_id === driverId);
    if (!driver) return driverId?.slice(0, 3)?.toUpperCase() || '???';
    const nameParts = driver.name.split(' ');
    return nameParts[nameParts.length - 1].slice(0, 3).toUpperCase();
  };
  
  // Sort object by value descending
  const sortByValue = (obj) => {
    return Object.entries(obj)
      .sort((a, b) => b[1] - a[1])
      .map(([key, value]) => ({ key, value }));
  };
  
  // Parse pole time to milliseconds for comparison
  const parseTimeToMs = (timeStr) => {
    if (!timeStr) return null;
    const match = timeStr.match(/(\d+):(\d+)\.(\d+)/);
    if (!match) return null;
    return parseInt(match[1]) * 60000 + parseInt(match[2]) * 1000 + parseInt(match[3]);
  };
  
  // Format time difference
  const formatTimeDiff = (diffMs) => {
    if (diffMs === 0) return 'Exact!';
    const sign = diffMs > 0 ? '+' : '-';
    const absDiff = Math.abs(diffMs);
    const mins = Math.floor(absDiff / 60000);
    const secs = Math.floor((absDiff % 60000) / 1000);
    const ms = absDiff % 1000;
    if (mins > 0) {
      return `${sign}${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
    }
    return `${sign}00:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  };

  const myPredictionsByRace = getMyPredictionsByRace();
  const raceStats = calculateRaceStatistics();

  return (
    <div className="min-h-screen bg-secondary py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-block bg-[#FDB913] rounded-full p-4 mb-4">
            <Users size={40} className="text-[#1a2332]" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-2">Voorspellingen</h1>
          <p className="text-muted-foreground">Bekijk je eigen voorspellingen en die van andere deelnemers</p>
        </div>

        <Tabs defaultValue="mine" className="w-full">
          <TabsList className="flex w-full mb-8 overflow-x-auto">
            <TabsTrigger value="mine" className="flex-1 min-w-fit px-2 sm:px-4 data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
              <User className="mr-1 sm:mr-2 flex-shrink-0" size={16} />
              <span className="hidden sm:inline">Mijn Voorspellingen</span>
              <span className="sm:hidden">Mijn</span>
            </TabsTrigger>
            <TabsTrigger value="statistics" className="flex-1 min-w-fit px-2 sm:px-4 data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
              <Trophy className="mr-1 sm:mr-2 flex-shrink-0" size={16} />
              <span className="hidden sm:inline">Statistieken</span>
              <span className="sm:hidden">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="race" className="flex-1 min-w-fit px-2 sm:px-4 data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
              <Flag className="mr-1 sm:mr-2 flex-shrink-0" size={16} />
              <span className="hidden sm:inline">Alle Deelnemers</span>
              <span className="sm:hidden">Alle</span>
            </TabsTrigger>
            <TabsTrigger value="championship" className="flex-1 min-w-fit px-2 sm:px-4 data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
              <Award className="mr-1 sm:mr-2 flex-shrink-0" size={16} />
              <span className="hidden sm:inline">Kampioenschap</span>
              <span className="sm:hidden">WK</span>
            </TabsTrigger>
          </TabsList>

          {/* My Predictions Tab */}
          <TabsContent value="mine">
            {!user ? (
              <Card className="border-2 border-orange-500 bg-orange-50">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-2 text-orange-800">
                    <AlertCircle size={20} />
                    <p>Log in om je voorspellingen te bekijken.</p>
                  </div>
                </CardContent>
              </Card>
            ) : myPredictions.length === 0 && !myChampionshipPrediction ? (
              <Card className="border-2 border-gray-300">
                <CardContent className="pt-6 text-center text-muted-foreground">
                  <p>Je hebt nog geen voorspellingen ingediend.</p>
                  <a href="/voorspel" className="text-[#FDB913] font-semibold hover:underline mt-2 inline-block">
                    Maak je eerste voorspelling →
                  </a>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Championship Prediction */}
                {myChampionshipPrediction && (
                  <Card className="border-2 border-[#FDB913]">
                    <CardHeader className="bg-gradient-to-r from-[#1a2332] to-[#2d3e50]">
                      <CardTitle className="text-[#FDB913] flex items-center justify-between">
                        <span className="flex items-center">
                          <Trophy className="mr-2" size={20} />
                          Kampioenschap Voorspelling 2026
                        </span>
                        {(myChampionshipPrediction.world_champion_points > 0 || myChampionshipPrediction.constructor_champion_points > 0) && (
                          <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm">
                            +{(myChampionshipPrediction.world_champion_points || 0) + (myChampionshipPrediction.constructor_champion_points || 0)} pts
                          </span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-[#1a2332] border border-[#FDB913] rounded-lg p-4">
                          <h3 className="font-bold text-[#FDB913] mb-2 flex items-center justify-between">
                            <span>🏆 Wereldkampioen</span>
                            {myChampionshipPrediction.is_scored && (
                              <span className={`text-xs px-2 py-0.5 rounded ${myChampionshipPrediction.world_champion_points > 0 ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                +{myChampionshipPrediction.world_champion_points || 0}
                              </span>
                            )}
                          </h3>
                          <p className="text-lg text-white">{getDriverName(myChampionshipPrediction.world_champion)}</p>
                        </div>
                        <div className="bg-[#1a2332] border border-blue-500 rounded-lg p-4">
                          <h3 className="font-bold text-blue-400 mb-2 flex items-center justify-between">
                            <span>🏁 Constructeurs Kampioen</span>
                            {myChampionshipPrediction.is_scored && (
                              <span className={`text-xs px-2 py-0.5 rounded ${myChampionshipPrediction.constructor_champion_points > 0 ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                +{myChampionshipPrediction.constructor_champion_points || 0}
                              </span>
                            )}
                          </h3>
                          <p className="text-lg text-white">{getTeamName(myChampionshipPrediction.constructor_champion)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Race Predictions */}
                {Object.keys(myPredictionsByRace).length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold text-foreground">Race Voorspellingen</h2>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={expandAllRaces}
                          className="text-sm text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-secondary"
                        >
                          Alles uitklappen
                        </button>
                        <span className="text-muted-foreground">|</span>
                        <button 
                          onClick={collapseAllRaces}
                          className="text-sm text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-secondary"
                        >
                          Alles inklappen
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {Object.entries(myPredictionsByRace).map(([raceId, preds]) => {
                        const raceScore = preds.race?.points_earned || 0;
                        const qualScore = preds.qualifying?.points_earned || 0;
                        const totalScore = raceScore + qualScore;
                        const hasScore = totalScore > 0;
                        const isRaceExpanded = expandedRaces[`my_${raceId}`];
                        const isScoreExpanded = expandedPredictions[`my_${raceId}`];
                        
                        // Combine breakdowns
                        const combinedBreakdown = {};
                        if (preds.race?.score_breakdown) {
                          combinedBreakdown.race = preds.race.score_breakdown;
                        }
                        if (preds.qualifying?.score_breakdown) {
                          combinedBreakdown.qualifying = preds.qualifying.score_breakdown;
                        }
                        const breakdownItems = renderScoreBreakdown(combinedBreakdown);
                        
                        return (
                        <Card key={raceId} className={`border ${isRaceExpanded ? 'border-[#FDB913]' : 'border-gray-300 dark:border-gray-700'} transition-colors overflow-hidden`}>
                          {/* Compact Header - Always visible */}
                          <div 
                            className="bg-secondary cursor-pointer px-4 py-3 flex items-center justify-between hover:bg-secondary/80"
                            onClick={() => toggleRaceExpand(`my_${raceId}`)}
                          >
                            <div className="flex items-center space-x-3">
                              <Flag className="text-[#FDB913]" size={18} />
                              <span className="font-semibold">{getRaceName(raceId)}</span>
                              <span className="text-xs text-muted-foreground">{getRaceDate(raceId)}</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              {hasScore ? (
                                <div className="flex items-center bg-green-500/20 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full text-sm">
                                  <Award size={14} className="mr-1" />
                                  <span className="font-bold">{totalScore} pts</span>
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">Nog niet gescoord</span>
                              )}
                              {isRaceExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                            </div>
                          </div>
                          
                          {/* Expandable Content */}
                          {isRaceExpanded && (
                          <CardContent className="pt-4 pb-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Qualifying Prediction */}
                              {preds.qualifying && (
                                <div className="bg-[#1a2332] border border-blue-500 rounded-lg p-4">
                                  <h3 className="font-bold text-white mb-3 flex items-center justify-between">
                                    <div className="flex items-center">
                                      <CheckCircle className="mr-2 text-green-500" size={16} />
                                      {seasonSettings.qualifying_p2p3_enabled ? 'Kwalificatie Top 3' : 'Kwalificatie'}
                                    </div>
                                    {qualScore > 0 && (
                                      <span className="text-sm bg-blue-500/30 text-blue-300 px-2 py-0.5 rounded">
                                        +{qualScore} pts
                                      </span>
                                    )}
                                  </h3>
                                  <div className="space-y-2">
                                    {preds.qualifying.qualifying?.map((driverId, i) => {
                                      // Get points for this qualifying position from score_breakdown (Q1_exact, Q2_exact, Q3_exact)
                                      const qualPositionPoints = preds.qualifying.score_breakdown?.[`Q${i + 1}_exact`] || 0;
                                      const isScored = preds.qualifying.is_scored;
                                      // Label based on whether P2/P3 is enabled: "Pole Position, P2, P3" or just show all as P1/P2/P3
                                      const posLabel = seasonSettings.qualifying_p2p3_enabled 
                                        ? `P${i + 1}:` 
                                        : (i === 0 ? 'Pole Position:' : `P${i + 1}:`);
                                      return (
                                        <div key={i} className="flex items-center justify-between">
                                          <div className="flex items-center space-x-2">
                                            <span className="font-bold text-blue-400 w-24">
                                              {posLabel}
                                            </span>
                                            <span className="text-white">{getDriverName(driverId)}</span>
                                          </div>
                                          {isScored && (
                                            <span className={`text-xs px-2 py-0.5 rounded ${qualPositionPoints > 0 ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                              +{qualPositionPoints}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {preds.qualifying.pole_time && (
                                      <div className="mt-3 pt-3 border-t border-blue-500/50 flex items-center justify-between">
                                        <div className="flex items-center">
                                          <Timer size={14} className="mr-2 text-blue-400" />
                                          <span className="font-semibold text-gray-400">Pole tijd: </span>
                                          <span className="text-white ml-1">{preds.qualifying.pole_time}</span>
                                        </div>
                                        {preds.qualifying.is_scored && (() => {
                                          // Check both regular pole_time points AND pole_time_fallback bonus
                                          const poleTimePoints = preds.qualifying.score_breakdown?.pole_time?.points || preds.qualifying.score_breakdown?.pole_time || 0;
                                          const poleFallbackPoints = preds.qualifying.score_breakdown?.pole_time_fallback?.points || 0;
                                          const totalPolePoints = poleTimePoints + poleFallbackPoints;
                                          return (
                                            <span className={`text-xs px-2 py-0.5 rounded ${totalPolePoints > 0 ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                              +{totalPolePoints}
                                            </span>
                                          );
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Race Prediction */}
                              {preds.race && (
                                <div className="bg-[#1a2332] border border-[#FDB913] rounded-lg p-4">
                                  <h3 className="font-bold text-[#FDB913] mb-3 flex items-center justify-between">
                                    <div className="flex items-center">
                                      <CheckCircle className="mr-2 text-green-500" size={16} />
                                      Race Top 5
                                    </div>
                                    {preds.race.is_scored && (
                                      <span className={`text-sm px-2 py-0.5 rounded ${raceScore > 0 ? 'bg-[#FDB913]/30 text-[#FDB913]' : 'bg-gray-500/20 text-gray-400'}`}>
                                        +{raceScore} pts
                                      </span>
                                    )}
                                  </h3>
                                  <div className="space-y-2">
                                    {preds.race.top5?.map((driverId, i) => {
                                      // Get points for this position from score_breakdown
                                      // Format: P1_verstappen: {predicted: 1, actual: 1, diff: 0, points: 25}
                                      const positionKey = `P${i + 1}_${driverId}`;
                                      const positionData = preds.race.score_breakdown?.[positionKey];
                                      const positionPoints = positionData?.points || 0;
                                      const isScored = preds.race.is_scored;
                                      return (
                                        <div key={i} className="flex items-center justify-between">
                                          <div className="flex items-center space-x-2">
                                            <span className="font-bold text-[#FDB913] w-8">P{i + 1}:</span>
                                            <span className="text-white">{getDriverName(driverId)}</span>
                                          </div>
                                          {isScored && (
                                            <span className={`text-xs px-2 py-0.5 rounded ${positionPoints > 0 ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                              +{positionPoints}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                  <div className="mt-3 pt-3 border-t border-[#FDB913]/50 space-y-1 text-sm">
                                    {preds.race.fastest_lap && (
                                      <p className="flex items-center justify-between text-white">
                                        <span className="flex items-center">
                                          <Zap size={12} className="mr-1 text-purple-400" />
                                          <span className="font-semibold text-gray-400">Snelste ronde:</span>
                                          <span className="ml-1">{getDriverName(preds.race.fastest_lap)}</span>
                                        </span>
                                        {preds.race.is_scored && (
                                          <span className={`text-xs px-2 py-0.5 rounded ${preds.race.score_breakdown?.fastest_lap > 0 ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                            +{preds.race.score_breakdown?.fastest_lap || 0}
                                          </span>
                                        )}
                                      </p>
                                    )}
                                    {preds.race.safety_car && (
                                      <p className="flex items-center justify-between text-white">
                                        <span className="flex items-center">
                                          <Car size={12} className="mr-1 text-orange-400" />
                                          <span className="font-semibold text-gray-400">Safety Car:</span>
                                          <span className="ml-1">{preds.race.safety_car === 'ja' ? 'Ja' : 'Nee'}</span>
                                        </span>
                                        {preds.race.is_scored && (
                                          <span className={`text-xs px-2 py-0.5 rounded ${preds.race.score_breakdown?.safety_car > 0 ? 'bg-orange-500/20 text-orange-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                            +{preds.race.score_breakdown?.safety_car || 0}
                                          </span>
                                        )}
                                      </p>
                                    )}
                                    {preds.race.virtual_safety_car && (
                                      <p className="flex items-center justify-between text-white">
                                        <span className="flex items-center">
                                          <Car size={12} className="mr-1 text-yellow-400" />
                                          <span className="font-semibold text-gray-400">Virtual SC:</span>
                                          <span className="ml-1">{preds.race.virtual_safety_car === 'ja' ? 'Ja' : 'Nee'}</span>
                                        </span>
                                        {preds.race.is_scored && (
                                          <span className={`text-xs px-2 py-0.5 rounded ${preds.race.score_breakdown?.virtual_safety_car > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                            +{preds.race.score_breakdown?.virtual_safety_car || 0}
                                          </span>
                                        )}
                                      </p>
                                    )}
                                    {preds.race.dnf_count !== undefined && preds.race.dnf_count !== null && (
                                      <p className="flex items-center justify-between text-white">
                                        <span className="flex items-center">
                                          <AlertCircle size={12} className="mr-1 text-red-400" />
                                          <span className="font-semibold text-gray-400">Aantal DNF:</span>
                                          <span className="ml-1">{preds.race.dnf_count}</span>
                                        </span>
                                        {preds.race.is_scored && (
                                          <span className={`text-xs px-2 py-0.5 rounded ${preds.race.score_breakdown?.dnf_count > 0 ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                            +{preds.race.score_breakdown?.dnf_count || 0}
                                          </span>
                                        )}
                                      </p>
                                    )}
                                    {preds.race.rain_race && (
                                      <p className="flex items-center justify-between text-white">
                                        <span className="flex items-center">
                                          <CloudRain size={12} className="mr-1 text-blue-400" />
                                          <span className="font-semibold text-gray-400">Regen:</span>
                                          <span className="ml-1">{preds.race.rain_race === 'ja' ? 'Ja' : 'Nee'}</span>
                                        </span>
                                        {preds.race.is_scored && (
                                          <span className={`text-xs px-2 py-0.5 rounded ${preds.race.score_breakdown?.rain_race > 0 ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                            +{preds.race.score_breakdown?.rain_race || 0}
                                          </span>
                                        )}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Sprint Predictions */}
                              {preds.race && (preds.race.sprint_winner || preds.race.sprint_pole_winner || preds.race.sprint_pole_time) && (
                                <div className="bg-[#1a2332] border border-purple-500 rounded-lg p-4 md:col-span-2" data-testid={`my-sprint-${raceId}`}>
                                  <h3 className="font-bold text-purple-300 mb-3 flex items-center text-sm">
                                    <Zap className="mr-2 text-purple-400" size={16} />
                                    Sprint Weekend Voorspellingen
                                  </h3>
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                                    {preds.race.sprint_winner && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-gray-400">Sprint Winnaar:</span>
                                        <span className="text-white font-medium">{getDriverName(preds.race.sprint_winner)}</span>
                                      </div>
                                    )}
                                    {preds.race.sprint_pole_winner && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-gray-400">Sprint Pole:</span>
                                        <span className="text-white font-medium">{getDriverName(preds.race.sprint_pole_winner)}</span>
                                      </div>
                                    )}
                                    {preds.race.sprint_pole_time && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-gray-400">Sprint Pole Tijd:</span>
                                        <span className="text-white font-mono">{preds.race.sprint_pole_time}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Score Breakdown - Expandable Section */}
                            {hasScore && isScoreExpanded && breakdownItems && breakdownItems.length > 0 && (
                              <div className="mt-4 pt-3 border-t border-dashed border-gray-600" data-testid={`my-score-breakdown-${raceId}`}>
                                <h4 className="font-bold text-foreground mb-3 flex items-center text-sm">
                                  <Award className="mr-2 text-green-600" size={16} />
                                  Puntenverdeling
                                </h4>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                                  {breakdownItems.map((item, i) => (
                                    <div 
                                      key={i} 
                                      className={`p-2 rounded border text-xs ${
                                        item.type === 'position' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' :
                                        item.type === 'qualifying' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' :
                                        item.type === 'pole' ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800' :
                                        'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                      }`}
                                    >
                                      <div className="flex justify-between items-start">
                                        <span className="text-foreground flex-1">{item.label}</span>
                                        <span className={`font-bold ml-1 ${
                                          item.points >= 20 ? 'text-green-600' :
                                          item.points >= 10 ? 'text-yellow-600' :
                                          'text-muted-foreground'
                                        }`}>+{item.points}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardContent>
                          )}
                        </Card>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Statistics Tab */}
          <TabsContent value="statistics">
            <Card className="border-2 border-[#FDB913] mb-6">
              <CardHeader>
                <CardTitle>Selecteer Race</CardTitle>
              </CardHeader>
              <CardContent>
                <Label className="mb-2 block">Race</Label>
                <Select value={selectedRace} onValueChange={handleRaceChange}>
                  <SelectTrigger data-testid="statistics-race-select">
                    <SelectValue placeholder="Selecteer een race" />
                  </SelectTrigger>
                  <SelectContent>
                    {races.map((race) => {
                      const deadlinePassed = isDeadlinePassed(race);
                      return (
                        <SelectItem key={race.race_id} value={race.race_id} data-testid={`race-option-${race.race_id}`}>
                          <span className="flex items-center gap-2">
                            {!deadlinePassed && <Clock size={14} className="text-orange-500 shrink-0" />}
                            {race.name} - {new Date(race.date).toLocaleDateString('nl-NL')}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Show deadline message for races before deadline */}
            {selectedRace && !isDeadlinePassed(races.find(r => r.race_id === selectedRace)) && (
              <Card className="border-2 border-orange-500/50 bg-orange-500/5 mb-6">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-3 text-orange-700 dark:text-orange-400">
                    <Clock size={20} />
                    <div>
                      <p className="font-semibold">Deadline nog niet verstreken</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        De voorspellingen en statistieken voor deze race worden zichtbaar na de start van VT1
                        {(() => { const r = races.find(r2 => r2.race_id === selectedRace); return r?.fp1?.date ? (
                          <span className="font-medium"> ({formatLocalTime(r.fp1.date, r.fp1.time)} op {new Date(`${r.fp1.date}T${r.fp1.time || '10:00'}:00Z`).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })})</span>
                        ) : null; })()}.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {error && (
              <Card className="border-2 border-orange-500 bg-orange-500/10 mb-6">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-2 text-orange-700 dark:text-orange-400">
                    <AlertCircle size={20} />
                    <p>{error}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {!error && raceStats && raceStats.total > 0 && (
              <>
                {/* Stats Overview */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                  {/* Rain */}
                  <Card className="border-2 border-blue-400">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center">
                        <CloudRain className="mr-2 text-blue-500" size={18} />
                        Regen
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-red-500">✖ Nee</span>
                        <span>{raceStats.rain.no} ({((raceStats.rain.no / raceStats.total) * 100).toFixed(0)}%)</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-green-500">✔ Ja</span>
                        <span>{raceStats.rain.yes} ({((raceStats.rain.yes / raceStats.total) * 100).toFixed(0)}%)</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Safety Car */}
                  <Card className="border-2 border-yellow-400">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center">
                        <Car className="mr-2 text-yellow-500" size={18} />
                        Safety Car
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-red-500">✖ Nee</span>
                        <span>{raceStats.safetyCar.no} ({((raceStats.safetyCar.no / raceStats.total) * 100).toFixed(0)}%)</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-green-500">✔ Ja</span>
                        <span>{raceStats.safetyCar.yes} ({((raceStats.safetyCar.yes / raceStats.total) * 100).toFixed(0)}%)</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* VSC */}
                  <Card className="border-2 border-orange-400">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center">
                        <AlertCircle className="mr-2 text-orange-500" size={18} />
                        Virtual SC
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-red-500">✖ Nee</span>
                        <span>{raceStats.vsc.no} ({((raceStats.vsc.no / raceStats.total) * 100).toFixed(0)}%)</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-green-500">✔ Ja</span>
                        <span>{raceStats.vsc.yes} ({((raceStats.vsc.yes / raceStats.total) * 100).toFixed(0)}%)</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* DNF */}
                  <Card className="border-2 border-red-400">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center">
                        <Zap className="mr-2 text-red-500" size={18} />
                        DNF Aantal
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {sortByValue(raceStats.dnf).slice(0, 4).map(({ key, value }) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="font-medium">{key}</span>
                          <span>{value} ({((value / raceStats.total) * 100).toFixed(0)}%)</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Pole Position (Kwalificatie P1) */}
                  <Card className="border-2 border-purple-400">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center">
                        <Flag className="mr-2 text-purple-500" size={18} />
                        Pole Position
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {sortByValue(raceStats.pole).slice(0, 4).map(({ key, value }) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="font-medium">{getDriverShortName(key)}</span>
                          <span>{value} ({((value / raceStats.total) * 100).toFixed(0)}%)</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Fastest Lap */}
                  <Card className="border-2 border-green-400">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center">
                        <Timer className="mr-2 text-green-500" size={18} />
                        Snelste Ronde
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {sortByValue(raceStats.fastestLap).slice(0, 4).map(({ key, value }) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="font-medium">{getDriverShortName(key)}</span>
                          <span>{value} ({((value / raceStats.total) * 100).toFixed(0)}%)</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Race Winner */}
                  <Card className="border-2 border-[#FDB913]">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center">
                        <Trophy className="mr-2 text-[#FDB913]" size={18} />
                        Racewinnaar
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {sortByValue(raceStats.raceWinner).slice(0, 4).map(({ key, value }) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="font-medium">{getDriverShortName(key)}</span>
                          <span>{value} ({((value / raceStats.total) * 100).toFixed(0)}%)</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Pole Time Average */}
                  {raceStats.poleTimes.length > 0 && (
                    <Card className="border-2 border-cyan-400">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center">
                          <Clock className="mr-2 text-cyan-500" size={18} />
                          Pole Rondetijd
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-lg font-bold text-center">
                          {(() => {
                            const times = raceStats.poleTimes.map(p => parseTimeToMs(p.time)).filter(t => t !== null);
                            if (times.length === 0) return '-';
                            const avg = times.reduce((a, b) => a + b, 0) / times.length;
                            const mins = Math.floor(avg / 60000);
                            const secs = Math.floor((avg % 60000) / 1000);
                            const ms = Math.floor(avg % 1000);
                            return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
                          })()}
                        </div>
                        <div className="text-xs text-center text-muted-foreground">(gemiddelde)</div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Pole Time Ranking */}
                {raceStats.poleTimes.length > 0 && (
                  <Card className="border-2 border-border mb-6">
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Clock className="mr-2 text-[#FDB913]" size={20} />
                        Pole Tijd Voorspellingen
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-2 px-2">#</th>
                              <th className="text-left py-2 px-2">Deelnemer</th>
                              <th className="text-left py-2 px-2">Voorspelde Tijd</th>
                            </tr>
                          </thead>
                          <tbody>
                            {raceStats.poleTimes
                              .map(p => ({ ...p, ms: parseTimeToMs(p.time) }))
                              .filter(p => p.ms !== null)
                              .sort((a, b) => a.ms - b.ms)
                              .map((p, idx) => (
                                <tr key={p.username} className="border-b border-border/50 hover:bg-secondary/50">
                                  <td className="py-2 px-2 text-muted-foreground">{idx + 1}</td>
                                  <td className="py-2 px-2 font-medium">{p.username}</td>
                                  <td className="py-2 px-2 font-mono">{p.time}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* All Predictions Table */}
                <Card className="border-2 border-border overflow-hidden">
                  <CardHeader className="bg-[#FDB913]/10">
                    <CardTitle className="flex items-center">
                      <Users className="mr-2 text-[#FDB913]" size={20} />
                      Alle Voorspellingen ({raceStats.total} deelnemers)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-secondary">
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-3 font-semibold sticky left-0 bg-secondary">Speler</th>
                            <th className="text-center py-3 px-2 font-semibold">Regen</th>
                            <th className="text-center py-3 px-2 font-semibold">SC</th>
                            <th className="text-center py-3 px-2 font-semibold">VSC</th>
                            <th className="text-center py-3 px-2 font-semibold">DNF</th>
                            <th className="text-center py-3 px-2 font-semibold">Pole</th>
                            <th className="text-center py-3 px-2 font-semibold">Snelste</th>
                            <th className="text-center py-3 px-2 font-semibold">Pole Tijd</th>
                            <th className="text-center py-3 px-2 font-semibold bg-[#FDB913]/20">P1</th>
                            <th className="text-center py-3 px-2 font-semibold bg-[#FDB913]/20">P2</th>
                            <th className="text-center py-3 px-2 font-semibold bg-[#FDB913]/20">P3</th>
                            <th className="text-center py-3 px-2 font-semibold bg-[#FDB913]/20">P4</th>
                            <th className="text-center py-3 px-2 font-semibold bg-[#FDB913]/20">P5</th>
                            {races.find(r => r.race_id === selectedRace)?.is_sprint_weekend && (
                              <>
                                <th className="text-center py-3 px-2 font-semibold bg-purple-500/20">Sprint</th>
                                <th className="text-center py-3 px-2 font-semibold bg-purple-500/20">S.Pole</th>
                                <th className="text-center py-3 px-2 font-semibold bg-purple-500/20">S.Tijd</th>
                              </>
                            )}
                            <th className="text-center py-3 px-2 font-semibold">Punten</th>
                          </tr>
                        </thead>
                        <tbody>
                          {racePredictions
                            .sort((a, b) => (b.total_score || 0) - (a.total_score || 0))
                            .map((pred, idx) => {
                              const racePred = pred.race_prediction || {};
                              const qualPred = pred.qualifying_prediction || {};
                              return (
                                <tr key={pred.username} className={`border-b border-border/50 hover:bg-secondary/50 ${idx % 2 === 0 ? 'bg-card' : 'bg-secondary/30'}`}>
                                  <td className="py-2 px-3 font-medium sticky left-0 bg-inherit">{pred.username}</td>
                                  <td className="text-center py-2 px-2">
                                    {racePred.rain_race === true || racePred.rain_race === 'ja' ? (
                                      <span className="text-green-500">✔</span>
                                    ) : racePred.rain_race === false || racePred.rain_race === 'nee' ? (
                                      <span className="text-red-500">✖</span>
                                    ) : '-'}
                                  </td>
                                  <td className="text-center py-2 px-2">
                                    {racePred.safety_car === true || racePred.safety_car === 'ja' ? (
                                      <span className="text-green-500">✔</span>
                                    ) : racePred.safety_car === false || racePred.safety_car === 'nee' ? (
                                      <span className="text-red-500">✖</span>
                                    ) : '-'}
                                  </td>
                                  <td className="text-center py-2 px-2">
                                    {racePred.virtual_safety_car === true || racePred.virtual_safety_car === 'ja' ? (
                                      <span className="text-green-500">✔</span>
                                    ) : racePred.virtual_safety_car === false || racePred.virtual_safety_car === 'nee' ? (
                                      <span className="text-red-500">✖</span>
                                    ) : '-'}
                                  </td>
                                  <td className="text-center py-2 px-2">{racePred.dnf_count ?? '-'}</td>
                                  <td className="text-center py-2 px-2 font-mono text-xs">{qualPred.qualifying?.[0] ? getDriverShortName(qualPred.qualifying[0]) : '-'}</td>
                                  <td className="text-center py-2 px-2 font-mono text-xs">{racePred.fastest_lap ? getDriverShortName(racePred.fastest_lap) : '-'}</td>
                                  <td className="text-center py-2 px-2 font-mono text-xs">{qualPred.pole_time || '-'}</td>
                                  <td className="text-center py-2 px-2 font-mono text-xs bg-[#FDB913]/10">{racePred.top5?.[0] ? getDriverShortName(racePred.top5[0]) : '-'}</td>
                                  <td className="text-center py-2 px-2 font-mono text-xs bg-[#FDB913]/10">{racePred.top5?.[1] ? getDriverShortName(racePred.top5[1]) : '-'}</td>
                                  <td className="text-center py-2 px-2 font-mono text-xs bg-[#FDB913]/10">{racePred.top5?.[2] ? getDriverShortName(racePred.top5[2]) : '-'}</td>
                                  <td className="text-center py-2 px-2 font-mono text-xs bg-[#FDB913]/10">{racePred.top5?.[3] ? getDriverShortName(racePred.top5[3]) : '-'}</td>
                                  <td className="text-center py-2 px-2 font-mono text-xs bg-[#FDB913]/10">{racePred.top5?.[4] ? getDriverShortName(racePred.top5[4]) : '-'}</td>
                                  {races.find(r => r.race_id === selectedRace)?.is_sprint_weekend && (
                                    <>
                                      <td className="text-center py-2 px-2 font-mono text-xs bg-purple-500/10">{racePred.sprint_winner ? getDriverShortName(racePred.sprint_winner) : '-'}</td>
                                      <td className="text-center py-2 px-2 font-mono text-xs bg-purple-500/10">{racePred.sprint_pole_winner ? getDriverShortName(racePred.sprint_pole_winner) : '-'}</td>
                                      <td className="text-center py-2 px-2 font-mono text-xs bg-purple-500/10">{racePred.sprint_pole_time || '-'}</td>
                                    </>
                                  )}
                                  <td className="text-center py-2 px-2 font-bold text-[#FDB913]">{pred.total_score || 0}</td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {!error && (!raceStats || raceStats.total === 0) && !selectedRace && (
              <Card className="border-2 border-border">
                <CardContent className="pt-6 text-center text-muted-foreground">
                  <p>Selecteer een race om de statistieken te bekijken.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Race Predictions - All Participants */}
          <TabsContent value="race">
            <Card className="border-2 border-[#FDB913] mb-6">
              <CardHeader>
                <CardTitle>Selecteer Race</CardTitle>
              </CardHeader>
              <CardContent>
                <Label className="mb-2 block">Race</Label>
                <Select value={selectedRace} onValueChange={handleRaceChange}>
                  <SelectTrigger data-testid="race-predictions-select">
                    <SelectValue placeholder="Selecteer een race" />
                  </SelectTrigger>
                  <SelectContent>
                    {races.map((race) => {
                      const deadlinePassed = isDeadlinePassed(race);
                      return (
                        <SelectItem key={race.race_id} value={race.race_id}>
                          <span className="flex items-center gap-2">
                            {!deadlinePassed && <Clock size={14} className="text-orange-500 shrink-0" />}
                            {race.name} - {new Date(race.date).toLocaleDateString('nl-NL')}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Show deadline message for races before deadline */}
            {selectedRace && !isDeadlinePassed(races.find(r => r.race_id === selectedRace)) && (
              <Card className="border-2 border-orange-500/50 bg-orange-500/5 mb-6">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-3 text-orange-700 dark:text-orange-400">
                    <Clock size={20} />
                    <div>
                      <p className="font-semibold">Deadline nog niet verstreken</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        De voorspellingen voor deze race worden zichtbaar na de start van VT1
                        {(() => { const r = races.find(r2 => r2.race_id === selectedRace); return r?.fp1?.date ? (
                          <span className="font-medium"> ({formatLocalTime(r.fp1.date, r.fp1.time)} op {new Date(`${r.fp1.date}T${r.fp1.time || '10:00'}:00Z`).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })})</span>
                        ) : null; })()}.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {error && (
              <Card className="border-2 border-orange-500 bg-orange-50 mb-6">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-2 text-orange-800">
                    <AlertCircle size={20} />
                    <p>{error}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {!error && racePredictions.length === 0 && (
              <Card className="border-2 border-gray-300">
                <CardContent className="pt-6 text-center text-muted-foreground">
                  <p>Nog geen voorspellingen beschikbaar voor deze race.</p>
                </CardContent>
              </Card>
            )}

            {!error && racePredictions.length > 0 && (
              <Card className="border-2 border-border overflow-hidden">
                {/* Header with search and count */}
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-secondary p-4 border-b border-border">
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      type="text"
                      placeholder="Zoek op naam..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="search-predictions"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users size={16} />
                    <span>{filteredPredictions.length} deelnemers</span>
                  </div>
                </div>

                {/* Compact Table */}
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="predictions-table">
                    <thead className="bg-[#1a2332] text-white">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold w-16">#</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Deelnemer</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold w-24">Punten</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold w-16">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredPredictions.map((pred, index) => {
                        const isExpanded = expandedPredictions[pred.username];
                        const breakdownItems = renderScoreBreakdown(pred.score_breakdown);
                        const hasScore = pred.total_score > 0;
                        
                        return (
                          <React.Fragment key={pred.username}>
                            {/* Main Row */}
                            <tr 
                              className={`hover:bg-secondary cursor-pointer transition-colors ${isExpanded ? 'bg-[#FDB913]/10' : ''}`}
                              onClick={() => toggleExpand(pred.username)}
                              data-testid={`prediction-row-${pred.username}`}
                            >
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                                  index < 3 ? 'bg-[#FDB913] text-[#1a2332]' : 'bg-secondary text-muted-foreground'
                                }`}>
                                  {index + 1}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-medium text-foreground">{pred.username}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold ${
                                  hasScore ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400' : 'bg-secondary text-muted-foreground'
                                }`}>
                                  {pred.total_score || 0} pts
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button className="text-muted-foreground hover:text-[#FDB913] transition-colors">
                                  {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                </button>
                              </td>
                            </tr>
                            
                            {/* Expanded Details Row */}
                            {isExpanded && (
                              <tr data-testid={`prediction-details-${pred.username}`}>
                                <td colSpan={4} className="p-0">
                                  <div className="bg-secondary border-t border-b border-border p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                      {/* Qualifying Prediction */}
                                      <div className="bg-[#1a2332] border border-blue-500/50 rounded-lg p-4 shadow-sm">
                                        <h3 className="font-bold text-white mb-3 flex items-center justify-between text-sm">
                                          <div className="flex items-center">
                                            <Flag className="mr-2 text-blue-400" size={14} />
                                            Kwalificatie
                                          </div>
                                          {pred.qualifying_prediction?.score > 0 && (
                                            <span className="text-xs bg-blue-500/30 text-blue-300 px-2 py-0.5 rounded">
                                              +{pred.qualifying_prediction.score} pts
                                            </span>
                                          )}
                                        </h3>
                                        <div className="space-y-1 text-sm">
                                          {pred.qualifying_prediction?.qualifying?.map((driverId, i) => {
                                            // Get points from score_breakdown (can be at top level or nested in qualifying)
                                            const qualBreakdown = pred.score_breakdown?.qualifying || pred.qualifying_prediction?.score_breakdown || {};
                                            const posPoints = qualBreakdown[`Q${i+1}_exact`] || 0;
                                            const hasScore = pred.qualifying_prediction?.score > 0 || pred.total_score > 0;
                                            // Label: "Pole Position" for first, "P2", "P3" for rest (matching Mijn Voorspellingen)
                                            const posLabel = seasonSettings.qualifying_p2p3_enabled 
                                              ? `P${i + 1}:` 
                                              : (i === 0 ? 'Pole Position:' : `P${i + 1}:`);
                                            return (
                                              <div key={i} className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                  <span className="font-bold text-blue-400 w-24">{posLabel}</span>
                                                  <span className="text-white">{getDriverName(driverId)}</span>
                                                </div>
                                                {hasScore && (
                                                  <span className={`text-xs px-1.5 py-0.5 rounded ${posPoints > 0 ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}`}>+{posPoints}</span>
                                                )}
                                              </div>
                                            );
                                          })}
                                          {pred.qualifying_prediction?.pole_time && (
                                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-blue-500/30">
                                              <div className="flex items-center space-x-2">
                                                <Timer size={12} className="text-blue-400" />
                                                <span className="text-gray-300">Pole: {pred.qualifying_prediction.pole_time}</span>
                                              </div>
                                              {(pred.qualifying_prediction?.score > 0 || pred.total_score > 0) && (() => {
                                                const qualBreakdown = pred.score_breakdown?.qualifying || {};
                                                // Check both regular pole_time points AND pole_time_fallback bonus
                                                const poleTimePoints = qualBreakdown.pole_time?.points || qualBreakdown.pole_time || 0;
                                                const poleFallbackPoints = qualBreakdown.pole_time_fallback?.points || 0;
                                                const totalPolePoints = poleTimePoints + poleFallbackPoints;
                                                return (
                                                  <span className={`text-xs px-1.5 py-0.5 rounded ${totalPolePoints > 0 ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                                    +{totalPolePoints}
                                                  </span>
                                                );
                                              })()}
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Race Prediction */}
                                      <div className="bg-[#1a2332] border border-[#FDB913]/50 rounded-lg p-4 shadow-sm">
                                        <h3 className="font-bold text-[#FDB913] mb-3 flex items-center justify-between text-sm">
                                          <div className="flex items-center">
                                            <Trophy className="mr-2 text-[#FDB913]" size={14} />
                                            Race Top 5
                                          </div>
                                          {pred.race_prediction?.score > 0 && (
                                            <span className="text-xs bg-[#FDB913]/30 text-[#FDB913] px-2 py-0.5 rounded">
                                              +{pred.race_prediction.score} pts
                                            </span>
                                          )}
                                        </h3>
                                        <div className="space-y-1 text-sm">
                                          {pred.race_prediction?.top5?.map((driverId, i) => {
                                            // Get points from score_breakdown (can be at top level or nested in race)
                                            const raceBreakdown = pred.score_breakdown?.race || pred.race_prediction?.score_breakdown || {};
                                            const positionKey = `P${i + 1}_${driverId}`;
                                            const positionData = raceBreakdown[positionKey];
                                            const posPoints = positionData?.points || 0;
                                            const hasScore = pred.race_prediction?.score > 0 || pred.total_score > 0;
                                            return (
                                              <div key={i} className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                  <span className="font-bold text-[#FDB913] w-6">P{i + 1}:</span>
                                                  <span className="text-white">{getDriverName(driverId)}</span>
                                                </div>
                                                {hasScore && (
                                                  <span className={`text-xs px-1.5 py-0.5 rounded ${posPoints > 0 ? 'bg-[#FDB913]/20 text-[#FDB913]' : 'bg-gray-500/20 text-gray-400'}`}>+{posPoints}</span>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                        {/* Extra voorspellingen - same style as Mijn Voorspellingen */}
                                        {(() => {
                                          const raceBreakdown = pred.score_breakdown?.race || pred.race_prediction?.score_breakdown || {};
                                          const hasScore = pred.race_prediction?.score > 0 || pred.total_score > 0;
                                          return (
                                        <div className="mt-3 pt-3 border-t border-[#FDB913]/50 space-y-1 text-sm">
                                          {pred.race_prediction?.fastest_lap && (
                                            <p className="flex items-center justify-between text-white">
                                              <span className="flex items-center">
                                                <Zap size={12} className="mr-1 text-purple-400" />
                                                <span className="font-semibold text-gray-400">Snelste ronde:</span>
                                                <span className="ml-1">{getDriverName(pred.race_prediction.fastest_lap)}</span>
                                              </span>
                                              {hasScore && (
                                                <span className={`text-xs px-2 py-0.5 rounded ${raceBreakdown.fastest_lap > 0 ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                                  +{raceBreakdown.fastest_lap || 0}
                                                </span>
                                              )}
                                            </p>
                                          )}
                                          {pred.race_prediction?.safety_car && (
                                            <p className="flex items-center justify-between text-white">
                                              <span className="flex items-center">
                                                <Car size={12} className="mr-1 text-orange-400" />
                                                <span className="font-semibold text-gray-400">Safety Car:</span>
                                                <span className="ml-1">{pred.race_prediction.safety_car === 'ja' ? 'Ja' : 'Nee'}</span>
                                              </span>
                                              {hasScore && (
                                                <span className={`text-xs px-2 py-0.5 rounded ${raceBreakdown.safety_car > 0 ? 'bg-orange-500/20 text-orange-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                                  +{raceBreakdown.safety_car || 0}
                                                </span>
                                              )}
                                            </p>
                                          )}
                                          {pred.race_prediction?.virtual_safety_car && (
                                            <p className="flex items-center justify-between text-white">
                                              <span className="flex items-center">
                                                <Car size={12} className="mr-1 text-yellow-400" />
                                                <span className="font-semibold text-gray-400">Virtual SC:</span>
                                                <span className="ml-1">{pred.race_prediction.virtual_safety_car === 'ja' ? 'Ja' : 'Nee'}</span>
                                              </span>
                                              {hasScore && (
                                                <span className={`text-xs px-2 py-0.5 rounded ${raceBreakdown.virtual_safety_car > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                                  +{raceBreakdown.virtual_safety_car || 0}
                                                </span>
                                              )}
                                            </p>
                                          )}
                                          {pred.race_prediction?.dnf_count !== undefined && pred.race_prediction?.dnf_count !== null && (
                                            <p className="flex items-center justify-between text-white">
                                              <span className="flex items-center">
                                                <AlertCircle size={12} className="mr-1 text-red-400" />
                                                <span className="font-semibold text-gray-400">Aantal DNF:</span>
                                                <span className="ml-1">{pred.race_prediction.dnf_count}</span>
                                              </span>
                                              {hasScore && (
                                                <span className={`text-xs px-2 py-0.5 rounded ${raceBreakdown.dnf_count > 0 ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                                  +{raceBreakdown.dnf_count || 0}
                                                </span>
                                              )}
                                            </p>
                                          )}
                                          {pred.race_prediction?.rain_race && (
                                            <p className="flex items-center justify-between text-white">
                                              <span className="flex items-center">
                                                <CloudRain size={12} className="mr-1 text-blue-400" />
                                                <span className="font-semibold text-gray-400">Regen:</span>
                                                <span className="ml-1">{pred.race_prediction.rain_race === 'ja' ? 'Ja' : 'Nee'}</span>
                                              </span>
                                              {hasScore && (
                                                <span className={`text-xs px-2 py-0.5 rounded ${raceBreakdown.rain_race > 0 ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                                  +{raceBreakdown.rain_race || 0}
                                                </span>
                                              )}
                                            </p>
                                          )}
                                        </div>
                                          );
                                        })()}
                                      </div>
                                    </div>

                                    {/* Sprint Predictions */}
                                    {(pred.race_prediction?.sprint_winner || pred.race_prediction?.sprint_pole_winner || pred.race_prediction?.sprint_pole_time) && (
                                      <div className="bg-[#1a2332] border border-purple-500/50 rounded-lg p-4 shadow-sm md:col-span-2" data-testid={`sprint-pred-${pred.username}`}>
                                        <h3 className="font-bold text-purple-300 mb-3 flex items-center text-sm">
                                          <Zap className="mr-2 text-purple-400" size={14} />
                                          Sprint Weekend
                                        </h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                                          {pred.race_prediction.sprint_winner && (
                                            <div className="flex items-center justify-between">
                                              <span className="text-gray-400">Sprint Winnaar:</span>
                                              <span className="text-white font-medium">{getDriverName(pred.race_prediction.sprint_winner)}</span>
                                            </div>
                                          )}
                                          {pred.race_prediction.sprint_pole_winner && (
                                            <div className="flex items-center justify-between">
                                              <span className="text-gray-400">Sprint Pole:</span>
                                              <span className="text-white font-medium">{getDriverName(pred.race_prediction.sprint_pole_winner)}</span>
                                            </div>
                                          )}
                                          {pred.race_prediction.sprint_pole_time && (
                                            <div className="flex items-center justify-between">
                                              <span className="text-gray-400">Sprint Pole Tijd:</span>
                                              <span className="text-white font-mono">{pred.race_prediction.sprint_pole_time}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* Championship Predictions */}
          <TabsContent value="championship">
            {championshipPredictions.length === 0 && (
              <Card className="border-2 border-orange-500 bg-orange-50">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-2 text-orange-800">
                    <AlertCircle size={20} />
                    <p>Kampioenschap voorspellingen zijn pas zichtbaar na de deadline (start eerste race).</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {championshipPredictions.length > 0 && (
              <div className="space-y-4">
                {championshipPredictions.map((pred, index) => (
                  <Card key={index} className="border-2 border-gray-300 hover:border-[#FDB913] transition-colors" data-testid={`champ-pred-${pred.username}`}>
                    <CardHeader className="bg-secondary">
                      <CardTitle className="text-lg flex items-center justify-between">
                        <span className="flex items-center space-x-2">
                          <Users size={20} className="text-[#FDB913]" />
                          <span>{pred.username}</span>
                        </span>
                        {pred.is_scored && pred.score > 0 && (
                          <span className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 px-3 py-1 rounded-full text-sm font-bold">
                            +{pred.score} pts
                          </span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-yellow-50 dark:bg-[#1a2332] border border-[#FDB913] rounded-lg p-4">
                          <h3 className="font-bold text-foreground dark:text-[#FDB913] mb-3">Wereldkampioen 2026</h3>
                          {(pred.world_champion_top3 && pred.world_champion_top3.filter(Boolean).length > 0) ? (
                            <div className="space-y-2">
                              {pred.world_champion_top3.map((driverId, i) => driverId ? (
                                <div key={i} className="flex items-center space-x-2">
                                  <span className="font-bold text-[#FDB913] w-8">P{i + 1}:</span>
                                  <span className="text-foreground dark:text-white">{getDriverName(driverId)}</span>
                                </div>
                              ) : null)}
                            </div>
                          ) : (
                            <p className="text-lg text-foreground dark:text-white">{getDriverName(pred.world_champion)}</p>
                          )}
                        </div>
                        <div className="bg-blue-50 dark:bg-[#1a2332] border border-blue-200 dark:border-blue-500 rounded-lg p-4">
                          <h3 className="font-bold text-foreground dark:text-blue-400 mb-3">Constructeurs Kampioen 2026</h3>
                          {(pred.constructor_champion_top3 && pred.constructor_champion_top3.filter(Boolean).length > 0) ? (
                            <div className="space-y-2">
                              {pred.constructor_champion_top3.map((teamId, i) => teamId ? (
                                <div key={i} className="flex items-center space-x-2">
                                  <span className="font-bold text-blue-400 w-8">P{i + 1}:</span>
                                  <span className="text-foreground dark:text-white">{getTeamName(teamId)}</span>
                                </div>
                              ) : null)}
                            </div>
                          ) : (
                            <p className="text-lg text-foreground dark:text-white">{getTeamName(pred.constructor_champion)}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Voorspellingen;
